import { type FunctionComponent, type HTMLAttributes, type SyntheticEvent, useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { type ProductOptionFieldsFragment } from '../../../../__generated__/graphql-client-types';
import { DEBOUNCED_THRESHOLD } from '../../../../constants/general';
import { isKeycodeField, useValidatorByName } from '../../../../helpers/product-helper/configuration/product-option.helper';
import { useSiteViewPreference } from '../../../../hooks/apollo/employee/employee.hooks';
import { type GroupSelection } from '../../../../types/product-option.types';
import { CloudinaryImage } from '../../../images/cloudinary-image/cloudinary-image.component';
import { TextInput, type TextInputProps } from '../../../inputs';
import { optionPill, productOption } from '../product-option.css';

export type OptionTextProps = {
	change: (currentOption: ProductOptionFieldsFragment, groupSelection: Pick<GroupSelection, 'valid'>) => void;
	params?: Record<string, any>;
	showCheckmark?: boolean;
} & ProductOptionFieldsFragment &
	Pick<HTMLAttributes<HTMLElement>, 'className'> &
	Pick<TextInputProps, 'testId' | 'placeholder' | 'maxLength'>;

/**
 * Option Text Component
 */
export const OptionText: FunctionComponent<OptionTextProps> = ({ className = 'w5', ...props }) => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { showCheckmark, change, params, testId, type, name, value, selected, resource, image, hasPricedOptions, slotId, ...inputProps } =
		props;
	const option: ProductOptionFieldsFragment = { type, name, value, selected, resource, image, hasPricedOptions, slotId };
	const { validator, validate, status } = useValidatorByName(isKeycodeField(name) ? 'Key Code' : name);
	const [validatorStatus, setValidatorStatus] = useState(validator);
	const [text, setText] = useState(value || '');
	const [debouncedText] = useDebounce(text, DEBOUNCED_THRESHOLD);
	const { showAsEmployee } = useSiteViewPreference();

	/**
	 * Change Event Handler
	 */
	const onChange = (current: ProductOptionFieldsFragment, ev: SyntheticEvent<HTMLInputElement>) => {
		const inputValue = ev.currentTarget.value;
		change({ ...current, value: inputValue }, { valid: inputValue.length === 0 });
		setText(inputValue);
	};

	/**
	 * Validate Input
	 */
	const validateInput = () => {
		const newStatus = validate(debouncedText, params?.variantId, showAsEmployee);
		setValidatorStatus({ ...validatorStatus, ...newStatus });
		change({ ...option, value: newStatus.invalid ? '' : debouncedText }, { valid: !newStatus.invalid });
	};

	// Input Text Change Only
	useEffect(() => {
		validateInput();
	}, [debouncedText, status?.data]);

	return (
		<div className={`${productOption} ${className ?? ''}`.trim()} data-testid={testId}>
			<div className={`flex flex-row items-start ${optionPill}`}>
				{image && (
					<CloudinaryImage
						className="pr3"
						publicID={image.id}
						description={image.description}
						options={{ width: 55, height: 55 }}
					/>
				)}
				<div className="tl w-100 h-100">
					<TextInput
						{...inputProps}
						testId="option-text-input"
						inputClassName="ph3 f6"
						inputMode="text"
						ariaLabel={name}
						value={value || text}
						invalid={validatorStatus.invalid}
						invalidMessage={validatorStatus.message}
						onChange={onChange.bind(null, option)}
					/>
					<p className="f7 mt1 theme-grey pl2">{name}</p>
				</div>
			</div>
		</div>
	);
};
